import { type MessageCreatorFormData, hasMessageContent } from '@/components/MessageCreator/utils';
import { toDateOrNull } from '@/helpers/date';
import { hasEmailEditorDesignContent } from '@/helpers/emailEditorDesign';
import { getDataFromLs, removeDataFromLs, setDataToLs } from '@/helpers/localStorage';
import { type EmailEditorDesign, LsStoreKey } from '@/types';

const buildMessageCreatorDraftStorageKey = ({ key }: { key: string }): string => {
  return `${LsStoreKey.MessageCreatorDraft}:${key}`;
};

export const MESSAGE_CREATOR_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
export const MESSAGE_CREATOR_DRAFT_AUTOSAVE_DEBOUNCE_MS = 1000;

export type MessageCreatorDraftView = 'form' | 'designer';

export type MessageCreatorDraftSnapshot = {
  userId: string;
  churchId: string;
  isImpersonating: boolean;
  formValues: MessageCreatorFormData;
  editorDesign: EmailEditorDesign | null;
  scheduleDate: Date | null;
  scheduleTime: string;
  currentView: MessageCreatorDraftView;
  isDesignClearedLocally: boolean;
  recipientLabels: Record<string, string>;
  updatedAt: number;
};

export type MessageCreatorDraftStorage = {
  get: (key: string) => Promise<MessageCreatorDraftSnapshot | null>;
  put: (key: string, snapshot: MessageCreatorDraftSnapshot) => Promise<{ ok: boolean }>;
  delete: (key: string) => Promise<{ ok: boolean }>;
};

type BuildMessageCreatorDraftKeyParams = {
  userId: string;
  churchId: string;
  scopeId: string;
  isImpersonating: boolean;
};

type BuildMessageCreatorDraftScopeIdParams = {
  churchId: string;
  allMembersGroupId?: string;
  isGroupScope?: boolean;
};

type IsMessageCreatorDraftExpiredParams = {
  updatedAt: number;
  now?: number;
  ttlMs?: number;
};

type IsBlankMessageCreateEntryParams = {
  searchParams: URLSearchParams;
  locationState: unknown;
};

type MessageCreatorDraftLocationState = {
  prefilledRecipientMembers?: unknown[];
  prefilledRecipientLabels?: Record<string, string>;
};

const createDefaultMessageCreatorDraftStorage = (): MessageCreatorDraftStorage => ({
  get: async (key) => {
    const snapshot = getDataFromLs(buildMessageCreatorDraftStorageKey({ key }));
    if (!snapshot || typeof snapshot !== 'object') {
      return null;
    }

    return snapshot as MessageCreatorDraftSnapshot;
  },
  put: async (key, snapshot) => {
    const storageKey = buildMessageCreatorDraftStorageKey({ key });
    setDataToLs(storageKey, snapshot);
    return { ok: Boolean(getDataFromLs(storageKey)) };
  },
  delete: async (key) => {
    removeDataFromLs(buildMessageCreatorDraftStorageKey({ key }));
    return { ok: true };
  },
});

let messageCreatorDraftStorage: MessageCreatorDraftStorage = createDefaultMessageCreatorDraftStorage();

export const setMessageCreatorDraftStorageForTests = ({
  storage,
}: {
  storage: MessageCreatorDraftStorage | null;
}): void => {
  messageCreatorDraftStorage = storage ?? createDefaultMessageCreatorDraftStorage();
};

export const buildMessageCreatorDraftKey = ({
  userId,
  churchId,
  scopeId,
  isImpersonating,
}: BuildMessageCreatorDraftKeyParams): string => {
  const impersonationSegment = isImpersonating ? 'impersonating' : 'self';
  return `${userId}::${churchId}::${scopeId}::${impersonationSegment}`;
};

export const buildMessageCreatorDraftScopeId = ({
  churchId,
  allMembersGroupId,
  isGroupScope = false,
}: BuildMessageCreatorDraftScopeIdParams): string => {
  if (isGroupScope && allMembersGroupId) {
    return `group:${allMembersGroupId}`;
  }

  return `church:${churchId}`;
};

export const isMessageCreatorDraftExpired = ({
  updatedAt,
  now = Date.now(),
  ttlMs = MESSAGE_CREATOR_DRAFT_TTL_MS,
}: IsMessageCreatorDraftExpiredParams): boolean => {
  if (!Number.isFinite(updatedAt) || updatedAt <= 0) {
    return true;
  }

  return now - updatedAt > ttlMs;
};

export const isBlankMessageCreateEntry = ({
  searchParams,
  locationState,
}: IsBlankMessageCreateEntryParams): boolean => {
  const draftId = searchParams.get('draftId');
  const templateId = searchParams.get('templateId');
  const recipients = searchParams.get('recipients');
  const recipientEmails = searchParams.get('recipientEmails');
  const openDesigner = searchParams.get('openDesigner');
  const editTemplate = searchParams.get('editTemplate');

  if (draftId || templateId || recipients || recipientEmails || openDesigner || editTemplate) {
    return false;
  }

  const state = locationState as MessageCreatorDraftLocationState | null;
  if (state?.prefilledRecipientMembers && state.prefilledRecipientMembers.length > 0) {
    return false;
  }

  if (state?.prefilledRecipientLabels && Object.keys(state.prefilledRecipientLabels).length > 0) {
    return false;
  }

  return true;
};

export const isValidMessageCreatorDraftSnapshot = ({
  snapshot,
}: {
  snapshot: unknown;
}): snapshot is MessageCreatorDraftSnapshot => {
  if (!snapshot || typeof snapshot !== 'object') {
    return false;
  }

  const value = snapshot as Partial<MessageCreatorDraftSnapshot>;
  if (typeof value.userId !== 'string' || value.userId.length === 0) {
    return false;
  }

  if (typeof value.churchId !== 'string' || value.churchId.length === 0) {
    return false;
  }

  if (typeof value.isImpersonating !== 'boolean') {
    return false;
  }

  if (!value.formValues || typeof value.formValues !== 'object') {
    return false;
  }

  if (typeof value.updatedAt !== 'number' || !Number.isFinite(value.updatedAt)) {
    return false;
  }

  if (value.currentView !== 'form' && value.currentView !== 'designer') {
    return false;
  }

  return true;
};

const omitDraftFileAttachments = ({
  snapshot,
}: {
  snapshot: MessageCreatorDraftSnapshot;
}): MessageCreatorDraftSnapshot => {
  return {
    ...snapshot,
    scheduleDate: toDateOrNull(snapshot.scheduleDate),
    formValues: {
      ...snapshot.formValues,
      formAttachments: [],
      announcementStartDate: toDateOrNull(snapshot.formValues.announcementStartDate),
      announcementEndDate: toDateOrNull(snapshot.formValues.announcementEndDate),
    },
  };
};

export const hasMessageCreatorDraftContent = ({ snapshot }: { snapshot: MessageCreatorDraftSnapshot }): boolean => {
  if (snapshot.formValues.messageTitle.trim().length > 0) {
    return true;
  }

  const messageText = snapshot.formValues.messageText || '';
  if (hasMessageContent({ value: messageText }) || /<!DOCTYPE|<html[\s>]/i.test(messageText)) {
    return true;
  }

  return hasEmailEditorDesignContent(snapshot.editorDesign);
};

export const getMessageCreatorDraft = async ({ key }: { key: string }): Promise<MessageCreatorDraftSnapshot | null> => {
  if (!key) {
    return null;
  }

  try {
    const snapshot = await messageCreatorDraftStorage.get(key);
    if (
      isValidMessageCreatorDraftSnapshot({ snapshot }) &&
      !isMessageCreatorDraftExpired({ updatedAt: snapshot.updatedAt }) &&
      hasMessageCreatorDraftContent({ snapshot })
    ) {
      return omitDraftFileAttachments({ snapshot });
    }

    return null;
  } catch {
    return null;
  }
};

const estimateMessageCreatorDraftSizeBytes = ({
  snapshot,
}: {
  snapshot: MessageCreatorDraftSnapshot;
}): {
  jsonBytes: number;
  attachmentBytes: number;
  totalBytes: number;
} => {
  const attachmentBytes = (snapshot.formValues.formAttachments ?? []).reduce((sum, file) => {
    if (!(file instanceof File)) {
      return sum;
    }

    return sum + file.size;
  }, 0);

  const jsonBytes = new Blob([
    JSON.stringify(snapshot, (_key, value) => {
      if (value instanceof File) {
        return {
          name: value.name,
          size: value.size,
          type: value.type,
        };
      }

      if (value instanceof Date) {
        return value.toISOString();
      }

      return value;
    }),
  ]).size;

  return {
    jsonBytes,
    attachmentBytes,
    totalBytes: jsonBytes + attachmentBytes,
  };
};

const logMessageCreatorDraftWrite = ({
  key,
  snapshot,
  ok,
}: {
  key: string;
  snapshot: MessageCreatorDraftSnapshot;
  ok: boolean;
}): void => {
  if (!import.meta.env.DEV) {
    return;
  }

  const { jsonBytes, attachmentBytes, totalBytes } = estimateMessageCreatorDraftSizeBytes({ snapshot });

  // Dev-only log so a draft write can be sized from the console.
  // eslint-disable-next-line no-console
  console.info('[messageCreatorDraft] localStorage put', {
    key,
    ok,
    title: snapshot.formValues.messageTitle,
    currentView: snapshot.currentView,
    updatedAt: new Date(snapshot.updatedAt).toISOString(),
    jsonKB: Number((jsonBytes / 1024).toFixed(1)),
    attachmentsKB: Number((attachmentBytes / 1024).toFixed(1)),
    approxTotalKB: Number((totalBytes / 1024).toFixed(1)),
  });
};

const designHasVisibleContent = ({ design }: { design: EmailEditorDesign | null }): boolean => {
  const rows = design?.body?.rows;
  if (!rows) {
    return false;
  }

  const pendingRows = [...rows];
  while (pendingRows.length > 0) {
    const row = pendingRows.pop();
    const columns = row?.columns ?? [];
    for (const column of columns) {
      for (const content of column.contents ?? []) {
        if (Array.isArray(content.columns) && content.columns.length > 0) {
          pendingRows.push({ columns: content.columns });
        }

        const text = typeof content.values?.text === 'string' ? content.values.text : '';
        if (hasMessageContent({ value: text })) {
          return true;
        }

        const imageSource = content.values?.src;
        if (typeof imageSource === 'string' && imageSource.trim().length > 0) {
          return true;
        }
      }
    }
  }

  return false;
};

const keepStoredMessageDesign = async ({
  key,
  snapshot,
}: {
  key: string;
  snapshot: MessageCreatorDraftSnapshot;
}): Promise<MessageCreatorDraftSnapshot> => {
  const stored = await messageCreatorDraftStorage.get(key);
  if (!isValidMessageCreatorDraftSnapshot({ snapshot: stored })) {
    return snapshot;
  }

  const storedSnapshot = omitDraftFileAttachments({ snapshot: stored });
  const keepDesign =
    designHasVisibleContent({ design: storedSnapshot.editorDesign }) &&
    !designHasVisibleContent({ design: snapshot.editorDesign });
  const keepMessageText =
    hasMessageContent({ value: storedSnapshot.formValues.messageText || '' }) &&
    !hasMessageContent({ value: snapshot.formValues.messageText || '' });

  if (!keepDesign && !keepMessageText) {
    return snapshot;
  }

  return {
    ...snapshot,
    editorDesign: keepDesign ? storedSnapshot.editorDesign : snapshot.editorDesign,
    formValues: {
      ...snapshot.formValues,
      messageText: keepMessageText ? storedSnapshot.formValues.messageText : snapshot.formValues.messageText,
    },
  };
};

export const putMessageCreatorDraft = async ({
  key,
  snapshot,
}: {
  key: string;
  snapshot: MessageCreatorDraftSnapshot;
}): Promise<{ ok: boolean }> => {
  if (!key || !isValidMessageCreatorDraftSnapshot({ snapshot })) {
    return { ok: false };
  }

  if (!hasMessageCreatorDraftContent({ snapshot })) {
    return { ok: true };
  }

  const snapshotWithoutAttachments = await keepStoredMessageDesign({
    key,
    snapshot: omitDraftFileAttachments({ snapshot }),
  });

  try {
    const result = await messageCreatorDraftStorage.put(key, snapshotWithoutAttachments);
    logMessageCreatorDraftWrite({ key, snapshot: snapshotWithoutAttachments, ok: result.ok });
    return result;
  } catch {
    logMessageCreatorDraftWrite({ key, snapshot: snapshotWithoutAttachments, ok: false });
    return { ok: false };
  }
};

export const deleteMessageCreatorDraft = async ({ key }: { key: string }): Promise<{ ok: boolean }> => {
  if (!key) {
    return { ok: false };
  }

  try {
    return await messageCreatorDraftStorage.delete(key);
  } catch {
    return { ok: false };
  }
};

export const createMessageCreatorDraftSnapshot = ({
  userId,
  churchId,
  isImpersonating,
  formValues,
  editorDesign,
  scheduleDate,
  scheduleTime,
  currentView,
  isDesignClearedLocally,
  recipientLabels,
  updatedAt = Date.now(),
}: {
  userId: string;
  churchId: string;
  isImpersonating: boolean;
  formValues: MessageCreatorFormData;
  editorDesign: EmailEditorDesign | null;
  scheduleDate: Date | null;
  scheduleTime: string;
  currentView: MessageCreatorDraftView;
  isDesignClearedLocally: boolean;
  recipientLabels: Record<string, string>;
  updatedAt?: number;
}): MessageCreatorDraftSnapshot => {
  return omitDraftFileAttachments({
    snapshot: {
      userId,
      churchId,
      isImpersonating,
      formValues,
      editorDesign,
      scheduleDate,
      scheduleTime,
      currentView,
      isDesignClearedLocally,
      recipientLabels,
      updatedAt,
    },
  });
};
